import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useSinglePortfolio } from 'pages/ClientOverview/pages/Portfolios/pages/SinglePortfolioView/context';
import {
    createCasePoAStorageKey,
    createNewCaseStorageKey,
} from 'datasource/CaseManagement/CreateCaseManager/CreateCaseManager';
import ButtonsBlockRow from 'components/ButtonsBlockRow';

import {
    Modal,
    Icon,
    Paragraph,
    Checkbox,
    Select,
    Infobox,
    Loader,
} from 'ui-library';

import {
    ICON_INFO_OUTLINE,
    ICON_TYPE_CLOSE_SLIM,
} from 'ui-library/components/Icon';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { useGenericAPIMutation } from 'hooks/mutations/useGenericAPIMutation';
import { portfolioSelector, usePortfolioSelector } from 'domain/Portfolio';
import { getClientId } from 'utils';
import { saveBase64File } from 'utils/file';
import { TableFilter, TableFilterItem } from 'components/TableFilter';

import Card from 'components/Card/Card';
import CasesTable from './components/CasesTable/CasesTable';

import {
    casesInProgressColumns,
    casesSubmittedColumns,
} from './components/CasesTable/columns';
import {
    adaptCasesInProgressData,
    adaptSubmittedCasesData,
    useGetSubmittedCasesData,
} from './components/CasesTable/adaptor';
import {
    docRefIdForPlanDataChangeForm,
    formatOwnersAndProxies,
    getStatusFilterOptions,
    getTypeFilterOptions,
} from './adapter';

import '../../styles/Table-ext.css';
import './Cases.css';

const Cases = () => {
    const { dfsClientId, portfolioId } = useParams();
    const contactId = getClientId(dfsClientId);

    const {
        t,
        i18n: { language },
    } = useTranslation();
    const navigate = useNavigate();
    const {
        isAzp, isAzpAlt, is3B, isPk,
    } = useSinglePortfolio();
    const { dataRaw: portfolio } = usePortfolioSelector(portfolioSelector);

    const {
        data: contactGroupDetailsData,
        isLoading: contactGroupDetailsDataLoading,
    } = useGetCustomService({
        service: 'customClientService',
        api: 'getContactGroupDetails',
        payload: { groupId: portfolio?.ContactGroupId },
        enabled: !!portfolio?.ContactGroupId,
        gcTime: 60 * 60 * 1000,
        staleTime: 30 * 60 * 1000,
    });

    const poaOptions = useMemo(
        () => formatOwnersAndProxies(contactGroupDetailsData?.data, t),
        [contactGroupDetailsData?.data, t],
    );

    const [modalOpen, setModalOpen] = useState<string | null>(null);
    const [shouldInitiateRetentionMail, setShouldInitiateRetentionMail] = useState(false);
    const [formType, setFormType] = useState<string | null>(null);
    const [poaOption, setPoAOption] = useState(null);
    const [typeFilter, setTypeFilter] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null);

    const {
        data: casesData,
        isLoading: caseDataLoading,
    } = useGetCustomService({
        service: 'customCaseManagementServices',
        api: 'getCasesByPortfolioId',
        payload: { portfolioId },
        enabled: !!portfolioId,
    });

    const {
        data: submittedCasesData,
        isLoading: submittedCaseDataLoading,
    } = useGetCustomService({
        service: 'customCaseManagementServices',
        api: 'getSubmittedCasesByPortfolioId',
        payload: { portfolioId },
        enabled: !!portfolioId,
    });

    const {
        data: listOfEmptyFormDocuments,
        isLoading: listOfEmptyFormDocumentsLoading,
    } = useGetCustomService({
        service: 'customCaseManagementServices',
        api: 'getListOfEmptyFormDocuments',
        payload: { language },
        enabled: !!language,
    });

    const emptyFormDocumentTypes = useMemo(
        () => listOfEmptyFormDocuments?.data?.documents?.map((document) => ({
            value: document?.dmsMetadataReferenceId,
            label: document?.documentName,
        })),
        [listOfEmptyFormDocuments?.data?.documents],
    ) || [];

    const {
        mutateAsync: downloadEmptyFormMutation,
        isPending: isFormDownloadInProgress,
    } = useGenericAPIMutation();

    const onNavigationClick = useCallback(
        ({ caseId }) => {
            navigate(`/client/${dfsClientId}/case/${caseId}`);
        },
        [dfsClientId, navigate],
    );

    const onNavigationClickSubmitted = useCallback(
        ({ caseId, externalId }) => {
            navigate(`/client/${dfsClientId}/case/submitted/${caseId}?externalId=${externalId}`);
        },
        [dfsClientId, navigate],
    );

    const casesInProgress = useMemo(
        () => adaptCasesInProgressData({
            data: casesData?.data?.cases,
            t,
            isPk,
            onNavigationClick,
        }),
        [casesData?.data?.cases, isPk, t, onNavigationClick],
    );

    const adaptedSubmittedCaseData = useMemo(
        () => adaptSubmittedCasesData({
            data: submittedCasesData?.data?.cases,
            t,
            isPk,
            onNavigationClick: onNavigationClickSubmitted,
        }),
        [submittedCasesData?.data?.cases, t, isPk, onNavigationClickSubmitted],
    );

    const { data: submittedCaseData } = useGetSubmittedCasesData({
        dataFromAPI: adaptedSubmittedCaseData,
        typeFilter,
        statusFilter,
    });

    const handleProceedToOpenCaseFlow = useCallback(() => {
        navigate(
            `/client/${dfsClientId}/portfolios/${portfolioId}/new-case/type-of-order`,
        );
    }, [dfsClientId, portfolioId, navigate]);

    const onCloseModal = useCallback(() => {
        setModalOpen(null);
        setShouldInitiateRetentionMail(false);
        setFormType(null);
    }, []);

    const OnOpenNewCase = useCallback(() => {
        sessionStorage.removeItem(createNewCaseStorageKey);
        sessionStorage.removeItem(createCasePoAStorageKey);
        if (isAzp || isAzpAlt) {
            setModalOpen('azp-modal');
        } else if (poaOptions?.length > 1) {
            setModalOpen('poa-modal');
        } else {
            handleProceedToOpenCaseFlow();
        }
    }, [isAzp, isAzpAlt, handleProceedToOpenCaseFlow, poaOptions]);

    const handleDownloadEmptyFormModal = useCallback(() => {
        setModalOpen('download-empty-form-modal');
    }, []);

    const downloadAndSaveForm = useCallback(
        async ({ documentReferenceId, docName, triggerRetentionEmail }) => {
            const payload = {
                documentReferenceId,
                portfolioId: +(portfolioId as string),
                contactId,
                triggerRetentionEmail,
            };

            const { data }: any = await downloadEmptyFormMutation({
                service: 'customCaseManagementServices',
                API: 'downloadEmptyForm',
                payload,
            });

            await saveBase64File({
                base64: data?.fileContent,
                name: `${docName}.pdf`,
            });
        },
        [portfolioId, downloadEmptyFormMutation, contactId],
    );

    const handleDownloadEmptyForm = useCallback(async () => {
        const docName = emptyFormDocumentTypes?.find((doc) => doc?.value === formType)
            ?.label || 'form';

        await downloadAndSaveForm({
            documentReferenceId: formType,
            docName,
            triggerRetentionEmail: shouldInitiateRetentionMail,
        });

        onCloseModal();
    }, [
        formType,
        shouldInitiateRetentionMail,
        downloadAndSaveForm,
        onCloseModal,
        emptyFormDocumentTypes,
    ]);

    const handleSetPoA = useCallback(() => {
        if (
            !poaOptions?.find((option) => option?.value === poaOption)
                ?.accessPolicy
        ) {
            sessionStorage.removeItem(createCasePoAStorageKey);
        } else {
            sessionStorage.setItem(
                createCasePoAStorageKey,
                JSON.stringify(poaOption),
            );
        }
        handleProceedToOpenCaseFlow();
    }, [poaOptions, poaOption, handleProceedToOpenCaseFlow]);

    const handlePlanDataChangeForm = useCallback(async () => {
        setFormType(docRefIdForPlanDataChangeForm);
        setModalOpen('download-empty-form-modal');
    }, []);

    const handleContinueWithoutAdjusting = useCallback(() => {
        if (poaOptions?.length > 1) {
            setModalOpen('poa-modal');
        } else {
            handleProceedToOpenCaseFlow();
        }
    }, [poaOptions, handleProceedToOpenCaseFlow]);

    if (!is3B) {
        return <span />;
    }

    return (
        <div className="case-orders">
            <div className="container">
                <ButtonsBlockRow
                    additionalButton={{
                        text: t('CaseManagementPage.downloadForm'),
                        onClick: handleDownloadEmptyFormModal,
                    }}
                    primaryButton={{
                        text: t('CaseManagementPage.OpenNewCase'),
                        loading: false,
                        onClick: OnOpenNewCase,
                        disabled: contactGroupDetailsDataLoading,
                    }}
                />
                <div className="fi-verticals">
                    <Card
                        title={t(
                            'portfolio.caseManagement.Card.CasesInProgress',
                        )}
                    >
                        {caseDataLoading ? (
                            <Loader />
                        ) : (
                            <CasesTable
                                columns={() => casesInProgressColumns({ t })}
                                data={casesInProgress}
                            />
                        )}
                    </Card>
                </div>

                <div className="fi-verticals mt-32 submitted-case-table">
                    <Card
                        title={t(
                            'portfolio.caseManagement.Card.SubmittedCases',
                        )}
                    >
                        <TableFilter>
                            <TableFilterItem>
                                <Select
                                    // hasSearch
                                    className="filters"
                                    placeholder={t(
                                        'portfolio.caseManagement.Card.SubmittedCases.Filter.ByType',
                                    )}
                                    options={getTypeFilterOptions(t)}
                                    value={typeFilter}
                                    onChange={(val) => setTypeFilter(val)}
                                />
                            </TableFilterItem>
                            <TableFilterItem>
                                <Select
                                    className="filters"
                                    placeholder={t(
                                        'portfolio.caseManagement.Card.SubmittedCases.Filter.ByStatus',
                                    )}
                                    options={getStatusFilterOptions(adaptedSubmittedCaseData, t)}
                                    value={statusFilter}
                                    onChange={(val) => setStatusFilter(val)}
                                />
                            </TableFilterItem>
                        </TableFilter>
                        {submittedCaseDataLoading ? (
                            <Loader />
                        ) : (
                            <CasesTable
                                className="mt-20"
                                columns={() => casesSubmittedColumns({ t })}
                                data={submittedCaseData}
                            />
                        )}
                    </Card>
                </div>
            </div>

            {modalOpen === 'azp-modal' && (
                <Modal
                    title={`${t('CaseManagementPage.Modal.Title')}:`}
                    visible
                    okText={t('CaseManagementPage.Modal.AZPModal.OkText')}
                    onOk={handlePlanDataChangeForm}
                    onCancel={handleContinueWithoutAdjusting}
                    onClose={onCloseModal}
                    width={543}
                    cancelText={t(
                        'CaseManagementPage.Modal.AZPModal.CancelText',
                    )}
                    okButtonProps={{ loading: isFormDownloadInProgress }}
                    className="case-management--azp-modal"
                    closable
                    closeIcon={<Icon type={ICON_TYPE_CLOSE_SLIM} size={24} />}
                    borderRadius
                >
                    <Paragraph className="mb-20">
                        {t('CaseManagementPage.Modal.Paragraph1')}
                    </Paragraph>

                    <Paragraph>
                        {t('CaseManagementPage.Modal.Paragraph2')}
                    </Paragraph>
                </Modal>
            )}

            {modalOpen === 'download-empty-form-modal' && (
                <Modal
                    title={t('CaseManagementPage.Modal.DownloadEmptyFormTitle')}
                    visible
                    onOk={handleDownloadEmptyForm}
                    onCancel={onCloseModal}
                    onClose={onCloseModal}
                    width={543}
                    cancelText={t('confirmation.cancel')}
                    okButtonProps={{
                        disabled: !formType,
                        loading: isFormDownloadInProgress,
                    }}
                    cancelButtonProps={{ type: 'link' }}
                    okText={t('confirmation.download')}
                    className="case-management--azp-modal case-management--download-empty-form-modal"
                    closable
                    closeIcon={<Icon type={ICON_TYPE_CLOSE_SLIM} size={24} />}
                    borderRadius
                >
                    <Paragraph className="mb-20">
                        {t(
                            'CaseManagementPage.Modal.DownloadEmptyFormParagraph1',
                        )}
                        :
                    </Paragraph>

                    {listOfEmptyFormDocumentsLoading ? (
                        <Loader />
                    ) : (
                        <Select
                            onChange={setFormType}
                            options={emptyFormDocumentTypes}
                            placeholder={t('position.pleaseSelect')}
                            data-cy="reasonForSale"
                            required
                            value={formType}
                        />
                    )}

                    <Checkbox
                        onChange={(e) => setShouldInitiateRetentionMail(e.target.checked)}
                        label={t(
                            'CaseManagementPage.Modal.PlanDataChangeFormCheckboxLabel',
                        )}
                    />
                </Modal>
            )}

            {modalOpen === 'poa-modal' && (
                <Modal
                    title={t('CaseManagementPage.Modal.PoAModal.Title')}
                    visible
                    onOk={handleSetPoA}
                    onClose={onCloseModal}
                    cancelText=""
                    width={543}
                    okButtonProps={{ disabled: !poaOption }}
                    okText={t('confirmation.continue')}
                    className="case-management--azp-modal case-management--download-empty-form-modal"
                    borderRadius
                >
                    <Infobox icon={ICON_INFO_OUTLINE} className="mb-24">
                        {t('CaseManagementPage.Modal.PoAModal.InfoText')}
                    </Infobox>

                    <Select
                        value={poaOption}
                        onChange={setPoAOption}
                        options={poaOptions}
                        placeholder={t('position.pleaseSelect')}
                        data-cy="reasonForSale"
                        required
                    />
                </Modal>
            )}
        </div>
    );
};

export default Cases;
